import React, { useMemo, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { chartAllColors } from '@/pages/opinion/constants';
import { reviewsFormat, yAxleFormat } from '@/pages/opinion/utils/value';
import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import './index.less';

type EChartsOptions = echarts.EChartsOption;

export interface CommonBarAndLineType {
  // 这个table是为了SubMenuCard组件能接收所有参数而做的妥协
  chartType: 'line' | 'bar';
  // chart的高度
  height?: number;
  //chart的tooltip浮窗处理
  tooltipFormatterFunc: (value: any) => string | HTMLElement | HTMLElement[];
  // x轴数据
  xAxisData: string[];
  // 实例数据，每一条线
  seriesDataArray: Array<{
    data: Array<any>;
    seriesOptions?: EChartsOptions['series'];
    color: string;
    legendName: string;
  }>;
  // 其他配置，可覆盖组件的默认配置
  otherConfig?: EChartsOptions;
  // 是否为堆叠图
  isStack: boolean;
  isCenterAxis: boolean;
  legendIcon?: (name: string, color: string) => string;
  isLeft: boolean;
  lineLegendFormatter?: (value: string) => string;
}

const TOTAL_SERIES_NAME = 'total总数';

const calcTotals = (
  seriesArray: CommonBarAndLineType['seriesDataArray'],
  selected?: Record<string, boolean>,
): number[] => {
  const totals: number[] = [];
  seriesArray.forEach(({ data, legendName }) => {
    if (selected && selected[legendName] === false) return;
    data.forEach((item, index) => {
      totals[index] = (item || 0) + (totals[index] || 0);
    });
  });
  return totals;
};

export default ({
  tooltipFormatterFunc,
  height,
  xAxisData,
  seriesDataArray,
  otherConfig = {},
  chartType,
  isStack,
  isCenterAxis,
  legendIcon,
  lineLegendFormatter,
  isLeft = true,
}: CommonBarAndLineType) => {
  const showTotalLabel = xAxisData.length < 9 && chartType === 'bar';
  const [legendSelected, setLegendSelected] = useState<
    Record<string, boolean>
  >({});

  const hasLegendSelection = Object.keys(legendSelected).length > 0;

  // 设置图形配置
  const makeupOptions = () => {
    const colorArr: Array<string> = [];
    const seriesData: any = [];
    const legendData: Array<any> = [];
    const totals = calcTotals(
      seriesDataArray,
      hasLegendSelection ? legendSelected : undefined,
    );
    seriesDataArray.forEach(
      ({ data, seriesOptions = {}, color, legendName }, index) => {
        const seriesItem: any = {
          data,
          type: chartType as any,
          name: legendName,
          itemStyle: {
            color: color || chartAllColors[index],
          },
          ...seriesOptions,
        };
        if (isStack) {
          seriesItem['stack'] = 'Total';
          seriesItem['areaStyle'] = {
            color: color,
          };
        }
        seriesData.push(seriesItem);
        legendData.push({
          name: legendName,
          icon: legendIcon ? legendIcon(color, legendName) : 'circle',
          itemStyle: {
            color: color,
          },
        });
        colorArr.push(color);
      },
    );
    if (showTotalLabel) {
      seriesData.unshift({
        name: TOTAL_SERIES_NAME,
        type: 'bar',
        data: totals,
        barGap: '-100%', // 左移100%，stack不再与需堆叠的柱子在一列
        label: {
          normal: {
            position: 'top',
            show: true,
            formatter: (c: any) => reviewsFormat(c?.value, 2),
          },
        },
        itemStyle: {
          normal: {
            color: 'rgba(128, 128, 128, 0)', // 为透明
          },
        },
      });
    }

    const yAxis = isCenterAxis
      ? {
        min: function (value: { max: number; min: number }) {
          const flag = Math.abs(value.max) > Math.abs(value.min);
          return flag ? -Math.abs(value.max) : Math.abs(value.min);
        },
        max: function (value: { max: number; min: number }) {
          const flag = Math.abs(value.max) > Math.abs(value.min);
          return flag ? Math.abs(value.max) : -Math.abs(value.min);
        },
      }
      : {};
    const opts: EChartsOptions = {
      tooltip: {
        formatter: (param) => tooltipFormatterFunc(param),
        appendTo: document.getElementById('echartAppendInfoId'),
        confine: false,
        position: (pos, params, dom, rect, size) => {
          // 鼠标在左侧时 tooltip 显示到右侧，鼠标在右侧时 tooltip 显示到左侧。
          const [mouseX, mouseY] = pos;
          const x = isLeft
            ? mouseX + 20
            : mouseX - (size?.contentSize?.[0] || 0) - 40;
          return [x, mouseY / 6];
        },
      },
      axisPointer: getAxisPointer((data) => {
        return moment(data.value).format('MM-DD');
      }),
      legend: {
        data: legendData,
        ...(hasLegendSelection ? { selected: legendSelected } : {}),
        formatter: (name) => {
          if (lineLegendFormatter instanceof Function) {
            return lineLegendFormatter(name);
          }
          return name;
        },
      },
      color: colorArr,
      xAxis: {
        type: 'category',
        boundaryGap: chartType === 'bar',
        axisLabel: {
          formatter: (value) => {
            return moment(value).format('MM-DD');
          },
        },
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E5E5E5',
          },
        },
        axisLabel: {
          formatter: (value) => {
            return yAxleFormat(value);
          },
        },
        ...yAxis,
      },
      grid: {
        top: 20,
        left: 20
      },
      series: seriesData,
      ...otherConfig,
    };

    return opts;
  };

  const options = useMemo(() => {
    return makeupOptions();
  }, [seriesDataArray, chartType, xAxisData, legendSelected]);

  const handleLegendSelectChanged = useCallback(
    (params: { selected?: Record<string, boolean> }) => {
      if (!showTotalLabel) return;
      setLegendSelected(params.selected || {});
    },
    [showTotalLabel],
  );

  const eventsDict = useMemo(
    () =>
      showTotalLabel
        ? { legendselectchanged: handleLegendSelectChanged }
        : {},
    [showTotalLabel, handleLegendSelectChanged],
  );

  return (
    <EChartCommon
      optionsProps={{ type: chartType }}
      height={height}
      options={options}
      eventsDict={eventsDict}
    ></EChartCommon>
  );
};
